import { useForm, FieldValues } from "react-hook-form";
import { LostUnits } from "@deliverr/commons-clients";

import { useEffect } from "react";
import { hasLowerCase, hasUpperCase } from "../../common/utils/config";
import { validationConfig } from "./LostUnitsFormConfig";
import { LostUnitsField } from "./LostUnitsForm.types";
import { inventoryClient } from "../LostUnitsList/LostUnitsList";
import moment from "moment";

let formError: string = "";

export function useLostUnitsForm() {
  const formHandler = useForm({
    mode: "onBlur",
  });

  const { trigger, formState } = formHandler;

  useEffect(() => {
    trigger();
  }, []);

  const ForceUpper = (field: string) => {
    const watchTag: string = formHandler.watch(field);

    useEffect(() => {
      if (watchTag && hasLowerCase.test(watchTag)) {
        formHandler.setValue(field, watchTag.toUpperCase());
      }
    }, [watchTag]);
  };

  const ForceLower = (field: string) => {
    const watchTag: string = formHandler.watch(field);

    useEffect(() => {
      if (watchTag && hasUpperCase.test(watchTag)) {
        formHandler.setValue(field, watchTag.toLowerCase());
      }
    }, [watchTag]);
  };

  ForceUpper(LostUnitsField.DSKU);
  ForceUpper(LostUnitsField.WAREHOUSE);
  ForceLower(LostUnitsField.SELLER);
  ForceLower(LostUnitsField.REPORTER);

  const newRecord = async (
    values: FieldValues
  ): Promise<LostUnits | undefined> => {
    return await inventoryClient.createLostUnits(values);
  };

  const isEmpty = (a: any): boolean =>
    a === null || a === undefined || a === "";

  const updateRecord = async (
    newValues: FieldValues,
    oldValues: LostUnits,
    recordId: number
  ): Promise<LostUnits | undefined> => {
    let diffValues: any = {};

    const compare = (a: any, b: any): boolean => {
      if (isEmpty(a) && isEmpty(b)) {
        return true;
      }

      if (isEmpty(a) || isEmpty(b)) {
        return false;
      }

      return a.toString() === b.toString();
    };

    for (const srcKey in newValues) {
      if (oldValues.hasOwnProperty(srcKey)) {
        if (!compare(newValues[srcKey], (oldValues as any)[srcKey])) {
          diffValues[srcKey] = newValues[srcKey];
        }
      }
    }

    if (Object.keys(diffValues).length === 0) {
      return {};
    }

    diffValues["id"] = recordId;
    return await inventoryClient.editLostUnits(diffValues);
  };

  const onFormSubmit = async (
    newValues: FieldValues,
    oldValues: LostUnits,
    recordId: number | undefined
  ) => {
    formError = "";

    const fixedValues: LostUnits = {
      ...(newValues as LostUnits),
      dsku: newValues.dsku.toUpperCase(),
      warehouseId: newValues.warehouseId?.toUpperCase(),
      sellerid: newValues.sellerid?.toLowerCase(),
      reportedBy: newValues.reportedBy?.toLowerCase(),
      lostAt: isEmpty(newValues.lostAt)
        ? moment(new Date()).format("YYYY-MM-DD")
        : newValues.lostAt,
    };

    let result = undefined;

    try {
      if (recordId === undefined) {
        result = await newRecord(fixedValues);
      } else {
        result = await updateRecord(fixedValues, oldValues, recordId);
      }
      if (Object.keys(result as any).length === 0) {
        formError = `No fields changed`;
      } else if (result === undefined || result.id === undefined) {
        formError = `Server error - check DSKU exists`;
      } else if (recordId !== undefined) {
        window.open("/LostUnits/List/", "_self");
      } else {
        formError = `Success entry created with id ${result.id}`;
      }
    } catch (e) {
      formError = `${e.toString()} --- ${e?.response?.data}`;
      //      formError = e?.response?.data?.error || e?.response?.data || e?.message || "Server Error";
    }
  };

  return {
    inventoryServiceError: null,
    errors: formHandler.errors,
    formState,
    formHandler,
    formError: () => formError,
    onFormSubmit,
    refs: {
      [LostUnitsField.DSKU]: formHandler.register(
        validationConfig[LostUnitsField.DSKU]
      ),
      [LostUnitsField.WAREHOUSE]: formHandler.register(
        validationConfig[LostUnitsField.WAREHOUSE]
      ),
      [LostUnitsField.SHIPPINGPLAN]: formHandler.register(
        validationConfig[LostUnitsField.SHIPPINGPLAN]
      ),
      [LostUnitsField.FC_ASN]: formHandler.register(
        validationConfig[LostUnitsField.FC_ASN]
      ),
      [LostUnitsField.LOST_QUANTITY]: formHandler.register(
        validationConfig[LostUnitsField.LOST_QUANTITY]
      ),
      [LostUnitsField.REASON]: formHandler.register(
        validationConfig[LostUnitsField.REASON]
      ),
      [LostUnitsField.NOTES]: formHandler.register(
        validationConfig[LostUnitsField.NOTES]
      ),
      [LostUnitsField.SELLER]: formHandler.register(
        validationConfig[LostUnitsField.SELLER]
      ),
      [LostUnitsField.REPORTER]: formHandler.register(
        validationConfig[LostUnitsField.REPORTER]
      ),
      [LostUnitsField.ASANA]: formHandler.register(
        validationConfig[LostUnitsField.ASANA]
      ),
      [LostUnitsField.ZENDESK]: formHandler.register(
        validationConfig[LostUnitsField.ZENDESK]
      ),
      [LostUnitsField.LOST_DATE]: formHandler.register(
        validationConfig[LostUnitsField.LOST_DATE]
      ),

      [LostUnitsField.CROSSDOCK_ASN]: formHandler.register(
        validationConfig[LostUnitsField.CROSSDOCK_ASN]
      ),
      [LostUnitsField.FOUND_QUANTITY]: formHandler.register(
        validationConfig[LostUnitsField.FOUND_QUANTITY]
      ),
      [LostUnitsField.REIMBURSED_AMOUNT]: formHandler.register(
        validationConfig[LostUnitsField.REIMBURSED_AMOUNT]
      ),
      [LostUnitsField.DISTRIBUTION]: formHandler.register(
        validationConfig[LostUnitsField.DISTRIBUTION]
      ),
    },
  };
}
